const SHA256_INITIAL = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
] as const;

const SHA256_K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
] as const;

function rotateRight(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount));
}

function asBytes(value: ArrayBuffer | ArrayBufferView): Uint8Array {
    if (value instanceof Uint8Array) return value;
    if (value instanceof ArrayBuffer) return new Uint8Array(value);
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
}

/** Incremental, allocation-bounded SHA-256 for browser File/Blob streams. */
export class IncrementalSha256 {
    private readonly state: number[] = [...SHA256_INITIAL];
    private readonly block = new Uint8Array(64);
    private readonly schedule = new Uint32Array(64);
    private blockLength = 0;
    private bytesHashed = 0;
    private finished = false;

    update(value: ArrayBuffer | ArrayBufferView): this {
        if (this.finished) throw new Error('SHA-256 digest has already been finalized.');
        const input = asBytes(value);
        this.bytesHashed += input.byteLength;
        if (!Number.isSafeInteger(this.bytesHashed)) {
            throw new RangeError('SHA-256 input exceeds the browser safe-integer byte range.');
        }

        let offset = 0;
        if (this.blockLength > 0) {
            const take = Math.min(64 - this.blockLength, input.byteLength);
            this.block.set(input.subarray(0, take), this.blockLength);
            this.blockLength += take;
            offset += take;
            if (this.blockLength === 64) {
                this.transform(this.block, 0);
                this.blockLength = 0;
            }
        }

        while (offset + 64 <= input.byteLength) {
            this.transform(input, offset);
            offset += 64;
        }
        if (offset < input.byteLength) {
            const remainder = input.subarray(offset);
            this.block.set(remainder, 0);
            this.blockLength = remainder.byteLength;
        }
        return this;
    }

    digestHex(): string {
        const digest = this.digest();
        return Array.from(digest)
            .map(byte => byte.toString(16).padStart(2, '0'))
            .join('');
    }

    digest(): Uint8Array {
        if (this.finished) throw new Error('SHA-256 digest has already been finalized.');
        this.finished = true;
        const bitLength = this.bytesHashed * 8;

        this.block[this.blockLength++] = 0x80;
        if (this.blockLength > 56) {
            this.block.fill(0, this.blockLength, 64);
            this.transform(this.block, 0);
            this.blockLength = 0;
        }
        this.block.fill(0, this.blockLength, 56);
        const high = Math.floor(bitLength / 0x100000000);
        const low = bitLength >>> 0;
        this.block[56] = (high >>> 24) & 0xff;
        this.block[57] = (high >>> 16) & 0xff;
        this.block[58] = (high >>> 8) & 0xff;
        this.block[59] = high & 0xff;
        this.block[60] = (low >>> 24) & 0xff;
        this.block[61] = (low >>> 16) & 0xff;
        this.block[62] = (low >>> 8) & 0xff;
        this.block[63] = low & 0xff;
        this.transform(this.block, 0);

        const output = new Uint8Array(32);
        for (let index = 0; index < this.state.length; index += 1) {
            const value = this.state[index] as number;
            const offset = index * 4;
            output[offset] = (value >>> 24) & 0xff;
            output[offset + 1] = (value >>> 16) & 0xff;
            output[offset + 2] = (value >>> 8) & 0xff;
            output[offset + 3] = value & 0xff;
        }
        return output;
    }

    private transform(input: Uint8Array, offset: number): void {
        const schedule = this.schedule;
        for (let index = 0; index < 16; index += 1) {
            const byteOffset = offset + index * 4;
            schedule[index] = (
                ((input[byteOffset] as number) << 24)
                | ((input[byteOffset + 1] as number) << 16)
                | ((input[byteOffset + 2] as number) << 8)
                | (input[byteOffset + 3] as number)
            ) >>> 0;
        }
        for (let index = 16; index < 64; index += 1) {
            const before15 = schedule[index - 15] as number;
            const before2 = schedule[index - 2] as number;
            const sigma0 = rotateRight(before15, 7) ^ rotateRight(before15, 18) ^ (before15 >>> 3);
            const sigma1 = rotateRight(before2, 17) ^ rotateRight(before2, 19) ^ (before2 >>> 10);
            schedule[index] = (
                (schedule[index - 16] as number)
                + sigma0
                + (schedule[index - 7] as number)
                + sigma1
            ) >>> 0;
        }

        let a = this.state[0] as number;
        let b = this.state[1] as number;
        let c = this.state[2] as number;
        let d = this.state[3] as number;
        let e = this.state[4] as number;
        let f = this.state[5] as number;
        let g = this.state[6] as number;
        let h = this.state[7] as number;

        for (let index = 0; index < 64; index += 1) {
            const sigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temp1 = (
                h + sigma1 + choose + (SHA256_K[index] as number) + (schedule[index] as number)
            ) >>> 0;
            const sigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (sigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }

        this.state[0] = ((this.state[0] as number) + a) >>> 0;
        this.state[1] = ((this.state[1] as number) + b) >>> 0;
        this.state[2] = ((this.state[2] as number) + c) >>> 0;
        this.state[3] = ((this.state[3] as number) + d) >>> 0;
        this.state[4] = ((this.state[4] as number) + e) >>> 0;
        this.state[5] = ((this.state[5] as number) + f) >>> 0;
        this.state[6] = ((this.state[6] as number) + g) >>> 0;
        this.state[7] = ((this.state[7] as number) + h) >>> 0;
    }
}
